/**
 * Float-hour time helpers (23.5 = 23:30), shared by the Department Configuration form and the Team
 * page's Wage Settings rule tables.
 *
 * Lives in `utils/` rather than beside the Settings page: the hours-rate table moved to Team → Wage
 * Settings (DAT-905), and a Team component reaching into a Settings page folder for a time parser
 * would tie the two surfaces together for no reason.
 */

const MAX_HOUR = 23;
const MAX_MINUTE = 59;
const MINUTES_PER_HOUR = 60;

/** "HH:MM-HH:MM" with exactly two minute digits on each side. */
const STRICT_TIME_PAIR_REGEX = /^(\d{1,2}):(\d{2})\s*-\s*(\d{1,2}):(\d{2})$/;

export function floatToTime(n: number): string {
	// Defensive: a non-numeric value (e.g. a raw "HH:MM:SS" from the server) previously rendered as
	// the literal string "NaN:NaN" in the input. Show an empty field instead.
	if (!Number.isFinite(n)) return "";
	const hours = Math.trunc(n);
	const minutes = Math.round((n - hours) * MINUTES_PER_HOUR);

	return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
}

export function timeToFloat(t: string): number {
	const [hours, minutes] = t.split(":");
	return Number(hours) + Number(minutes) / MINUTES_PER_HOUR;
}

export const validateTimeInput = (value: string | null, onValid: (timeFloat: number) => void): void => {
	if (value && typeof value === "string") {
		const match = value.match(/^(\d{1,2}):(\d{2})$/);
		if (match) {
			const [, hours, minutes] = match;
			const hour = parseInt(hours, 10);
			const min = parseInt(minutes, 10);
			if (hour >= 0 && hour <= MAX_HOUR && min >= 0 && min <= MAX_MINUTE) {
				onValid(timeToFloat(value));
			}
		}
	}
};

/** The four numbers of an "HH:MM-HH:MM" window, as typed. */
export type TimeWindowParts = { startHour: number; startMinute: number; endHour: number; endMinute: number };

/** Why a window is invalid. Each maps to its own caption key where one is shown. */
export type TimeWindowError = "format" | "notNumbers" | "hoursRange" | "minutesRange" | "startEqualsEnd";

/** Splits an "HH:MM-HH:MM" window into its numbers; `null` when the text is not in that format. */
export const parseTimeWindow = (value: string): TimeWindowParts | null => {
	const match = value.match(STRICT_TIME_PAIR_REGEX);
	if (!match) return null;
	const [startHour, startMinute, endHour, endMinute] = match.slice(1).map((part) => parseInt(part, 10));
	return { startHour, startMinute, endHour, endMinute };
};

/** What is wrong with a window's numbers, checked in the order the caption reports them; `null` when valid. */
export const timeWindowPartsError = ({ startHour, startMinute, endHour, endMinute }: TimeWindowParts): TimeWindowError | null => {
	if ([startHour, startMinute, endHour, endMinute].some(Number.isNaN)) return "notNumbers";
	if (startHour < 0 || startHour > MAX_HOUR || endHour < 0 || endHour > MAX_HOUR) return "hoursRange";
	if (startMinute < 0 || startMinute > MAX_MINUTE || endMinute < 0 || endMinute > MAX_MINUTE) return "minutesRange";
	if (startHour === endHour && startMinute === endMinute) return "startEqualsEnd";
	return null;
};

/** What is wrong with a typed "HH:MM-HH:MM" window; `null` when valid. */
export const timeWindowError = (value: string): TimeWindowError | null => {
	const parts = parseTimeWindow(value);
	return parts ? timeWindowPartsError(parts) : "format";
};

/**
 * Parses an "HH:MM-HH:MM" window and calls `onValid` with both ends as float hours. Invalid input
 * (bad format, out-of-range hours/minutes, or start equal to end) is rejected by not calling back.
 * Hours and minutes are range-checked BEFORE converting, so "18:99" is rejected instead of normalized.
 */
export const validateTimeBoundaries = (timePair: string, onValid: (startHourFloat: number, endHourFloat: number) => void): void => {
	const parts = parseTimeWindow(timePair);
	if (!parts || timeWindowPartsError(parts) !== null) return;
	onValid(parts.startHour + parts.startMinute / MINUTES_PER_HOUR, parts.endHour + parts.endMinute / MINUTES_PER_HOUR);
};
